import { useQueryClient } from '@tanstack/react-query';

import { requestBuilder } from '../../http/RequestBuilder';
import type {
    FruitSliceAccessLevel,
    FruitSlicePermission,
} from '../types/FruitSlicePermissionTypes';

const permissionsPath = (id: string): string =>
    `/api/admin/fruit-slices/${encodeURIComponent(id)}/permissions`;

export const fruitSlicePermissionsQueryKey = (id: string): string =>
    `fruitSlicePermissions/${id}`;

export const useListFruitSlicePermissions = requestBuilder.query<
    FruitSlicePermission[],
    { id: string }
>({
    endpoint: ({ id }) => permissionsPath(id),
    queryKey: ({ id }) => fruitSlicePermissionsQueryKey(id),
});

const useUpsertFruitSlicePermission = requestBuilder.mutation<
    FruitSlicePermission,
    { email: string; accessLevel: FruitSliceAccessLevel },
    { id: string }
>({
    endpoint: ({ id }) => permissionsPath(id),
    method: 'POST',
});

const useDeleteFruitSlicePermission = requestBuilder.mutation<
    unknown,
    undefined,
    { id: string; adminUserId: string }
>({
    endpoint: ({ id, adminUserId }) =>
        `${permissionsPath(id)}/${encodeURIComponent(adminUserId)}`,
    method: 'DELETE',
});

export const useManageFruitSlicePermissions = (sliceId: string) => {
    const queryClient = useQueryClient();
    const upsertPermission = useUpsertFruitSlicePermission();
    const deletePermission = useDeleteFruitSlicePermission();

    const invalidatePermissions = async (): Promise<void> => {
        await queryClient.invalidateQueries({
            queryKey: [fruitSlicePermissionsQueryKey(sliceId)],
        });
    };

    return {
        upsertPermissionAsync: async (
            email: string,
            accessLevel: FruitSliceAccessLevel,
        ): Promise<FruitSlicePermission> => {
            const result = await upsertPermission.mutateAsync({
                body: { email, accessLevel },
                urlParams: { id: sliceId },
            });
            await invalidatePermissions();
            return result;
        },
        deletePermissionAsync: async (adminUserId: string): Promise<void> => {
            await deletePermission.mutateAsync({
                urlParams: { id: sliceId, adminUserId },
            });
            await invalidatePermissions();
        },
        isPending: upsertPermission.isPending || deletePermission.isPending,
    };
};
